/**
 * <dbr-shipping-calculator> — busca o endereço pelo CEP (ViaCEP) e
 * consulta as opções de frete reais da loja (/cart/shipping_rates.json).
 * Reaproveitável em vários pontos da página ao mesmo tempo (bloco na PDP
 * + carrinho/drawer) — tudo escopado a `this` (nunca document.querySelector
 * global), pra duas instâncias não brigarem pelo mesmo elemento quando o
 * drawer abre por cima da PDP.
 *
 * A API de fretes do Shopify exige pelo menos 1 item no carrinho pra
 * retornar taxas — se o carrinho estiver vazio (comum na PDP, antes de
 * adicionar o produto), adiciona a variante atual temporariamente, faz a
 * consulta, e limpa o carrinho de novo em seguida (mesmo padrão usado por
 * calculadoras de frete de outras lojas Shopify BR).
 */
// Guard: o script é incluído por mais de um ponto (PDP, drawer, página do carrinho) — carregado
// 2x, uma declaração `class` no escopo global quebraria com "already been declared".
if (!customElements.get('dbr-shipping-calculator')) {
  var DBR_SHIPPING_DEFAULT_SUFFIX = 'dia(s) útil(eis)';
  var DBR_SHIPPING_TRUCK_ICON =
    '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.2" stroke-linecap="round" stroke-linejoin="round">' +
    '<path d="M1.5 5.5h12v10h-12z"/><path d="M13.5 9h4.2l3.3 3.6v2.9h-7.5"/>' +
    '<circle cx="6" cy="17.5" r="1.8"/><circle cx="17" cy="17.5" r="1.8"/></svg>';

  class DbrShippingCalculator extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('[data-cep-input]');
      this.submitButton = this.querySelector('[data-cep-submit]');
      this.responseEl = this.querySelector('[data-cep-response]');
      if (!this.input || !this.submitButton || !this.responseEl) return;

      this.variantId = this.dataset.variantId || null;
      this.strings = {
        loading: this.dataset.loadingText || 'Calculando frete...',
        invalid: this.dataset.errorInvalid || 'Digite um CEP válido.',
        noRates: this.dataset.errorNoRates || 'Não entregamos para esse CEP.',
        deliveryPrefix: this.dataset.deliveryPrefix || 'Entrega em',
        deliveryDaysSuffix: this.dataset.deliveryDaysSuffix || DBR_SHIPPING_DEFAULT_SUFFIX,
        free: this.dataset.freeLabel || 'Grátis',
        resultsTitle: this.dataset.resultsTitle || 'Opções de entrega',
        fastest: this.dataset.fastestLabel || 'Mais rápida',
      };

      this.input.addEventListener('input', this.maskInput.bind(this));
      this.input.addEventListener('keydown', this.onKeydown.bind(this));
      this.submitButton.addEventListener('click', this.onSubmit.bind(this));
    }

    maskInput(event) {
      var digits = event.target.value.replace(/\D/g, '').slice(0, 8);
      if (digits.length > 5) digits = digits.slice(0, 5) + '-' + digits.slice(5);
      event.target.value = digits;
    }

    onKeydown(event) {
      if (event.key === 'Enter') {
        event.preventDefault();
        this.onSubmit();
      }
    }

    onSubmit() {
      if (this.pending) return;
      this.calculate();
    }

    async calculate() {
      var zip = this.input.value.replace(/\D/g, '');
      this.renderMessage('');

      if (zip.length !== 8) {
        this.renderMessage(this.strings.invalid);
        return;
      }

      this.pending = true;
      this.setLoading(true);

      try {
        var address = await this.lookupZip(zip);
        if (!address || address.erro) {
          this.renderMessage(this.strings.invalid);
          return;
        }

        var addedTempItem = false;
        var cart = await this.getCart();
        if (cart.item_count === 0) {
          if (!this.variantId) {
            this.renderMessage(this.strings.noRates);
            return;
          }
          await this.addItem(this.variantId);
          addedTempItem = true;
        }

        var rates = await this.getShippingRates(zip, address);
        this.pushDataLayerEvent(zip, address, rates);
        this.renderRates(rates);

        if (addedTempItem) {
          await fetch('/cart/clear.js', { method: 'POST' });
        }
      } catch (error) {
        this.renderMessage(this.strings.noRates);
      } finally {
        this.pending = false;
        this.setLoading(false);
      }
    }

    // Mapa UF -> nome completo do estado: a API de shipping_rates do
    // Shopify espera o nome da província por extenso (igual ao cadastrado
    // nas zonas de frete da loja), não a sigla que o ViaCEP retorna.
    static get UF_TO_STATE() {
      return {
        AC: 'Acre',
        AL: 'Alagoas',
        AM: 'Amazonas',
        AP: 'Amapá',
        BA: 'Bahia',
        CE: 'Ceará',
        DF: 'Distrito Federal',
        ES: 'Espírito Santo',
        GO: 'Goiás',
        MA: 'Maranhão',
        MG: 'Minas Gerais',
        MS: 'Mato Grosso do Sul',
        MT: 'Mato Grosso',
        PA: 'Pará',
        PB: 'Paraíba',
        PE: 'Pernambuco',
        PI: 'Piauí',
        PR: 'Paraná',
        RJ: 'Rio de Janeiro',
        RN: 'Rio Grande do Norte',
        RO: 'Rondônia',
        RR: 'Roraima',
        RS: 'Rio Grande do Sul',
        SC: 'Santa Catarina',
        SE: 'Sergipe',
        SP: 'São Paulo',
        TO: 'Tocantins',
      };
    }

    async lookupZip(zip) {
      var response = await fetch('https://viacep.com.br/ws/' + zip + '/json/');
      return response.json();
    }

    async getCart() {
      var response = await fetch('/cart.js');
      return response.json();
    }

    async addItem(variantId) {
      return fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: variantId, quantity: 1 }),
      });
    }

    async getShippingRates(zip, address) {
      var state = DbrShippingCalculator.UF_TO_STATE[address.uf] || address.uf;
      var params = new URLSearchParams({
        'shipping_address[zip]': zip,
        'shipping_address[country]': 'Brazil',
        'shipping_address[province]': state,
      });
      if (address.localidade) params.set('shipping_address[city]', address.localidade);

      var response = await fetch('/cart/shipping_rates.json?' + params.toString());
      if (!response.ok) return [];
      var data = await response.json();
      return data.shipping_rates || [];
    }

    pushDataLayerEvent(zip, address, rates) {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        event: 'calculate_shipping',
        shipping: {
          zip: zip,
          state: address.uf || null,
          city: address.localidade || null,
          success: rates.length > 0,
          rates_count: rates.length,
          cheapest_rate: rates.length ? Math.min.apply(null, rates.map(function (rate) { return Number(rate.price); })) : null,
        },
      });
    }

    setLoading(isLoading) {
      this.classList.toggle('is-loading', isLoading);
      this.input.disabled = isLoading;
      this.submitButton.disabled = isLoading;
      if (isLoading) this.renderMessage(this.strings.loading);
    }

    renderMessage(message) {
      this.responseEl.innerHTML = '';
      if (!message) return;
      var p = document.createElement('p');
      p.className = 'dbr-shipping-calculator__message';
      p.textContent = message;
      this.responseEl.appendChild(p);
    }

    renderRates(rates) {
      this.responseEl.innerHTML = '';
      if (!rates.length) {
        this.renderMessage(this.strings.noRates);
        return;
      }

      var self = this;
      var result = document.createElement('div');
      result.className = 'dbr-shipping-calculator__result';

      var header = document.createElement('div');
      header.className = 'dbr-shipping-calculator__result-header';
      var title = document.createElement('span');
      title.textContent = this.strings.resultsTitle;
      var cep = document.createElement('span');
      cep.className = 'dbr-shipping-calculator__result-cep';
      cep.textContent = this.input.value;
      header.appendChild(title);
      header.appendChild(cep);
      result.appendChild(header);

      // "Mais rápida" só quando há mais de uma opção e um único vencedor no prazo mínimo.
      var minDays = rates.map(function (rate) { return self.getDays(rate)[0]; });
      var validDays = minDays.filter(function (d) { return d !== null; });
      var fastest = validDays.length > 1 ? Math.min.apply(null, validDays) : null;
      if (fastest !== null && validDays.filter(function (d) { return d === fastest; }).length > 1) fastest = null;

      var list = document.createElement('ul');
      list.className = 'dbr-shipping-calculator__rates';

      rates.forEach(function (rate, index) {
        var isFree = Number(rate.price) === 0;
        var days = self.getDays(rate);

        var item = document.createElement('li');
        item.className = 'dbr-shipping-calculator__rate' + (isFree ? ' is-free' : '');
        item.style.setProperty('--dbr-rate-index', index);

        var icon = document.createElement('span');
        icon.className = 'dbr-shipping-calculator__rate-icon';
        icon.setAttribute('aria-hidden', 'true');
        icon.innerHTML = DBR_SHIPPING_TRUCK_ICON;
        item.appendChild(icon);

        var info = document.createElement('span');
        info.className = 'dbr-shipping-calculator__rate-info';

        var name = document.createElement('span');
        name.className = 'dbr-shipping-calculator__rate-name';
        name.textContent = rate.name;
        if (fastest !== null && days[0] === fastest) {
          var tag = document.createElement('span');
          tag.className = 'dbr-shipping-calculator__rate-tag';
          tag.textContent = self.strings.fastest;
          name.appendChild(tag);
        }
        info.appendChild(name);

        if (days[0] !== null) {
          var delivery = document.createElement('span');
          delivery.className = 'dbr-shipping-calculator__rate-delivery';
          delivery.textContent = self.formatDelivery(days[0], days[1]);
          info.appendChild(delivery);
        }
        item.appendChild(info);

        var price = document.createElement('span');
        price.className = 'dbr-shipping-calculator__rate-price';
        price.textContent = isFree ? self.strings.free : self.formatPrice(Number(rate.price));
        item.appendChild(price);

        list.appendChild(item);
      });

      result.appendChild(list);
      this.responseEl.appendChild(result);
    }

    // [min, max] em dias úteis — /cart/shipping_rates.json devolve delivery_days como [min, max].
    getDays(rate) {
      var d = rate.delivery_days;
      if (!d || !d.length) return [null, null];
      var min = Number(d[0]);
      var max = d.length > 1 ? Number(d[d.length - 1]) : min;
      return [min, max];
    }

    formatDelivery(min, max) {
      var count = min === max ? String(min) : min + ' a ' + max;
      var suffix = this.strings.deliveryDaysSuffix;
      // Sufixo padrão "dia(s) útil(eis)" → plural de verdade; sufixo customizado no tema é respeitado.
      if (suffix === DBR_SHIPPING_DEFAULT_SUFFIX) suffix = max === 1 ? 'dia útil' : 'dias úteis';
      return this.strings.deliveryPrefix + ' ' + count + ' ' + suffix;
    }

    formatPrice(price) {
      return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(price);
    }
  }

  customElements.define('dbr-shipping-calculator', DbrShippingCalculator);
}
